import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Loader2, Save, Plus, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { contentService, adminService } from '../../services/api';

const AdminEmployerInfo = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  
  const [content, setContent] = useState({
    // Chinese content
    employerRequirements: [
      {
        title: '僱主資格',
        titleEn: 'Employer Qualifications',
        items: ['香港永久性居民或香港居民', '年滿18歲', '擁有香港住址證明', '有穩定收入'],
        itemsEn: ['Hong Kong permanent resident or resident', 'At least 18 years old', 'Proof of Hong Kong address', 'Stable income']
      },
      {
        title: '住宿要求',
        titleEn: 'Accommodation Requirements',
        items: ['提供獨立房間', '合理的生活設施', '安全及衞生的環境', '合符法例要求'],
        itemsEn: ['Provide separate room', 'Reasonable living facilities', 'Safe and hygienic environment', 'Comply with legal requirements']
      },
      {
        title: '費用負擔',
        titleEn: 'Cost Responsibilities',
        items: ['每月工資：港幣$5,195或以上', '膳食津貼：港幣$1,236或提供膳食', '免費住宿', '來回機票及保險'],
        itemsEn: ['Monthly salary: HK$5,195 or above', 'Food allowance: HK$1,236 or provide meals', 'Free accommodation', 'Round-trip airfare and insurance']
      }
    ],
    employerDocuments: [
      '香港身份證副本',
      '住址證明文件',
      '收入證明',
      '僱主聲明書',
      '住所平面圖（如需要）'
    ],
    employerDocumentsEn: [
      'Copy of Hong Kong ID card',
      'Proof of address',
      'Proof of income',
      'Employer declaration',
      'Floor plan of residence (if required)'
    ],
    employerResponsibilities: [
      { icon: '📄', title: '合約義務', titleEn: 'Contract Obligations', description: '遵守僱傭合約條款，按時支付工資及相關費用', descriptionEn: 'Comply with employment contract terms, pay wages and related expenses on time' },
      { icon: '🛡️', title: '保險責任', titleEn: 'Insurance Responsibility', description: '為外傭購買法定保險，保障雙方權益', descriptionEn: 'Purchase statutory insurance for helper to protect both parties' },
      { icon: '👥', title: '合理待遇', titleEn: 'Fair Treatment', description: '按法例給予休息日、有薪假期及合理工時', descriptionEn: 'Provide rest days, paid leave and reasonable working hours as required by law' }
    ],
    employerTips: [
      { title: '明確期望', titleEn: 'Clear Expectations', content: '清楚說明工作要求及期望，避免誤會', contentEn: 'Clearly communicate job requirements and expectations to avoid misunderstandings' },
      { title: '良好溝通', titleEn: 'Good Communication', content: '保持開放态度，及時溝通任何問題', contentEn: 'Maintain an open attitude, communicate any issues promptly' },
      { title: '尊重對方', titleEn: 'Mutual Respect', content: '尊重外傭的文化背景及個人隐私', contentEn: 'Respect the helper\'s cultural background and personal privacy' },
      { title: '定期評估', titleEn: 'Regular Assessment', content: '定期評估工作表現，給予正面反饋', contentEn: 'Regularly assess work performance and provide positive feedback' },
      { title: '提供培訓', titleEn: 'Provide Training', content: '適當提供培訓機會，提升工作技能', contentEn: 'Provide appropriate training opportunities to improve job skills' },
      { title: '合法僱傭', titleEn: 'Legal Employment', content: '确保所有程序合法，保障僱傭權益', contentEn: 'Ensure all procedures are legal to protect employment rights' }
    ],
    employerLegalInfo: [
      '最低工資：2025年外傭最低工資為港幣$5,195。',
      '休息日：每星期至少1天休息日，如工作需按法例計算資銀。',
      '法定假期：每年享有12天法定假期。',
      '有薪年假：合約每滿12個月享有7天有薪年假。',
      '保險要求：必須購買僱員補償保險及醫療保險。',
      '終止合約：需按合約約定給予通知期或代通知期薪金。'
    ],
    employerLegalInfoEn: [
      'Minimum Wage: The minimum wage for foreign domestic helpers in 2025 is HK$5,195.',
      'Rest Days: At least 1 rest day per week. Work on rest days must be compensated as required by law.',
      'Statutory Holidays: 12 statutory holidays per year.',
      'Annual Leave: 7 days paid annual leave for every 12 months of service.',
      'Insurance Requirements: Must purchase employee compensation insurance and medical insurance.',
      'Contract Termination: Notice period or payment in lieu as specified in the contract.'
    ]
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await contentService.get();
      if (response.data) {
        // Ensure employerRequirements have English fields
        const employerRequirements = (response.data.employerRequirements || []).map(req => ({
          ...req,
          titleEn: req.titleEn || '',
          itemsEn: req.itemsEn || req.items?.map(() => '') || []
        }));
        
        // Ensure employerDocumentsEn matches employerDocuments length
        const employerDocuments = response.data.employerDocuments || [];
        const employerDocumentsEn = response.data.employerDocumentsEn || [];
        while (employerDocumentsEn.length < employerDocuments.length) {
          employerDocumentsEn.push('');
        }
        
        // Ensure employerResponsibilities have English fields
        const employerResponsibilities = (response.data.employerResponsibilities || []).map(r => ({
          ...r,
          titleEn: r.titleEn || '',
          descriptionEn: r.descriptionEn || ''
        }));
        
        // Ensure employerTips have English fields
        const employerTips = (response.data.employerTips || []).map(t => ({
          ...t,
          titleEn: t.titleEn || '',
          contentEn: t.contentEn || ''
        }));
        
        // Ensure employerLegalInfoEn matches employerLegalInfo length
        const employerLegalInfo = response.data.employerLegalInfo || [];
        const employerLegalInfoEn = response.data.employerLegalInfoEn || [];
        while (employerLegalInfoEn.length < employerLegalInfo.length) {
          employerLegalInfoEn.push('');
        }
        
        setContent(prev => ({
          employerRequirements: employerRequirements.length > 0 ? employerRequirements : prev.employerRequirements,
          employerDocuments: employerDocuments.length > 0 ? employerDocuments : prev.employerDocuments,
          employerDocumentsEn: employerDocumentsEn,
          employerResponsibilities: employerResponsibilities.length > 0 ? employerResponsibilities : prev.employerResponsibilities,
          employerTips: employerTips.length > 0 ? employerTips : prev.employerTips,
          employerLegalInfo: employerLegalInfo.length > 0 ? employerLegalInfo : prev.employerLegalInfo,
          employerLegalInfoEn: employerLegalInfoEn,
        }));
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentContent = await contentService.get();
      const updatedContent = {
        ...currentContent.data,
        ...content
      };
      
      await adminService.content.update(updatedContent);
      
      toast({
        title: '成功 / Success',
        description: '僱主須知已保存 / Employer info saved'
      });
    } catch (error) {
      console.error('Error saving:', error);
      toast({
        title: '錯誤 / Error',
        description: '保存失敗，請再試一次 / Save failed, please try again',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Requirement Section handlers
  const updateRequirementTitle = (index, value) => {
    const newReqs = [...content.employerRequirements];
    newReqs[index].title = value;
    setContent({ ...content, employerRequirements: newReqs });
  };

  const updateRequirementTitleEn = (index, value) => {
    const newReqs = [...content.employerRequirements];
    newReqs[index].titleEn = value;
    setContent({ ...content, employerRequirements: newReqs });
  };

  const updateRequirementItem = (reqIndex, itemIndex, value) => {
    const newReqs = [...content.employerRequirements];
    newReqs[reqIndex].items[itemIndex] = value;
    setContent({ ...content, employerRequirements: newReqs });
  };

  const updateRequirementItemEn = (reqIndex, itemIndex, value) => {
    const newReqs = [...content.employerRequirements];
    if (!newReqs[reqIndex].itemsEn) {
      newReqs[reqIndex].itemsEn = [];
    }
    newReqs[reqIndex].itemsEn[itemIndex] = value;
    setContent({ ...content, employerRequirements: newReqs });
  };

  const addRequirementItem = (reqIndex) => {
    const newReqs = [...content.employerRequirements];
    newReqs[reqIndex].items.push('');
    if (!newReqs[reqIndex].itemsEn) {
      newReqs[reqIndex].itemsEn = [];
    }
    newReqs[reqIndex].itemsEn.push('');
    setContent({ ...content, employerRequirements: newReqs });
  };

  const removeRequirementItem = (reqIndex, itemIndex) => {
    const newReqs = [...content.employerRequirements];
    newReqs[reqIndex].items = newReqs[reqIndex].items.filter((_, i) => i !== itemIndex);
    if (newReqs[reqIndex].itemsEn) {
      newReqs[reqIndex].itemsEn = newReqs[reqIndex].itemsEn.filter((_, i) => i !== itemIndex);
    }
    setContent({ ...content, employerRequirements: newReqs });
  };

  // Document handlers
  const updateDocument = (index, value) => {
    const newDocs = [...content.employerDocuments];
    newDocs[index] = value;
    setContent({ ...content, employerDocuments: newDocs });
  };

  const updateDocumentEn = (index, value) => {
    const newDocsEn = [...(content.employerDocumentsEn || [])];
    newDocsEn[index] = value;
    setContent({ ...content, employerDocumentsEn: newDocsEn });
  };

  const addDocument = () => {
    setContent({ 
      ...content, 
      employerDocuments: [...content.employerDocuments, ''],
      employerDocumentsEn: [...(content.employerDocumentsEn || []), '']
    });
  };

  const removeDocument = (index) => {
    setContent({ 
      ...content, 
      employerDocuments: content.employerDocuments.filter((_, i) => i !== index),
      employerDocumentsEn: (content.employerDocumentsEn || []).filter((_, i) => i !== index)
    });
  };

  // Responsibility handlers
  const updateResponsibility = (index, field, value) => {
    const newResps = [...content.employerResponsibilities];
    newResps[index][field] = value;
    setContent({ ...content, employerResponsibilities: newResps });
  };

  // Tip handlers
  const updateTip = (index, field, value) => {
    const newTips = [...content.employerTips];
    newTips[index][field] = value;
    setContent({ ...content, employerTips: newTips });
  };

  const addTip = () => {
    setContent({
      ...content,
      employerTips: [...content.employerTips, { title: '', titleEn: '', content: '', contentEn: '' }]
    });
  };

  const removeTip = (index) => {
    setContent({
      ...content,
      employerTips: content.employerTips.filter((_, i) => i !== index)
    });
  };

  // Legal info handlers
  const updateLegalInfo = (index, value) => {
    const newInfo = [...content.employerLegalInfo];
    newInfo[index] = value;
    setContent({ ...content, employerLegalInfo: newInfo });
  };

  const updateLegalInfoEn = (index, value) => {
    const newInfoEn = [...(content.employerLegalInfoEn || [])];
    newInfoEn[index] = value;
    setContent({ ...content, employerLegalInfoEn: newInfoEn });
  };

  const addLegalInfo = () => {
    setContent({ 
      ...content, 
      employerLegalInfo: [...content.employerLegalInfo, ''],
      employerLegalInfoEn: [...(content.employerLegalInfoEn || []), '']
    });
  };

  const removeLegalInfo = (index) => {
    setContent({ 
      ...content, 
      employerLegalInfo: content.employerLegalInfo.filter((_, i) => i !== index),
      employerLegalInfoEn: (content.employerLegalInfoEn || []).filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#00b6a5' }}>僱主須知管理 / Employer Info Management</h1>
        <p className="text-gray-600 mt-2">編輯僱主須知頁面內容（中英文）/ Edit employer info content (Chinese & English)</p>
      </div>

      <div className="space-y-6">
        {/* Requirements Sections */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>僱主基本要求（3個分類）/ Employer Requirements</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {content.employerRequirements.map((req, reqIndex) => (
              <div key={reqIndex} className="p-4 bg-gray-50 rounded-lg space-y-4">
                {/* Chinese title */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">分類標題（中文）</label>
                    <Input
                      value={req.title}
                      onChange={(e) => updateRequirementTitle(reqIndex, e.target.value)}
                      placeholder="分類標題"
                      className="font-semibold"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-700">Category Title (English)</label>
                    <Input
                      value={req.titleEn || ''}
                      onChange={(e) => updateRequirementTitleEn(reqIndex, e.target.value)}
                      placeholder="Category title in English"
                      className="border-blue-300"
                    />
                  </div>
                </div>

                {/* Items */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium">項目 / Items</label>
                  {req.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="p-3 bg-white rounded border space-y-2">
                      <div className="flex items-center gap-2">
                        <Input
                          value={item}
                          onChange={(e) => updateRequirementItem(reqIndex, itemIndex, e.target.value)}
                          placeholder="輸入要求（中文）..."
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRequirementItem(reqIndex, itemIndex)}
                          className="text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="bg-blue-50 p-2 rounded">
                        <Input
                          value={req.itemsEn?.[itemIndex] || ''}
                          onChange={(e) => updateRequirementItemEn(reqIndex, itemIndex, e.target.value)}
                          placeholder="Enter requirement (English)..."
                          className="border-blue-300"
                        />
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => addRequirementItem(reqIndex)}>
                    <Plus className="h-4 w-4 mr-1" /> 新增項目 / Add Item
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Documents */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>所需文件 / Required Documents</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {content.employerDocuments.map((doc, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={doc}
                    onChange={(e) => updateDocument(index, e.target.value)}
                    placeholder="輸入文件名稱（中文）..."
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeDocument(index)}
                    className="text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="bg-blue-50 p-2 rounded">
                  <Input
                    value={content.employerDocumentsEn?.[index] || ''}
                    onChange={(e) => updateDocumentEn(index, e.target.value)}
                    placeholder="Enter document name (English)..."
                    className="border-blue-300"
                  />
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addDocument}>
              <Plus className="h-4 w-4 mr-2" /> 新增文件 / Add Document
            </Button>
          </CardContent>
        </Card>

        {/* Responsibilities */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>僱主責任（3個）/ Employer Responsibilities</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {content.employerResponsibilities.map((resp, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded space-y-4">
                {/* Chinese fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">圖示 (emoji)</label>
                    <Input
                      value={resp.icon}
                      onChange={(e) => updateResponsibility(index, 'icon', e.target.value)}
                      placeholder="📄"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">標題（中文）</label>
                    <Input
                      value={resp.title}
                      onChange={(e) => updateResponsibility(index, 'title', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">描述（中文）</label>
                    <Input
                      value={resp.description}
                      onChange={(e) => updateResponsibility(index, 'description', e.target.value)}
                    />
                  </div>
                </div>
                {/* English fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-3 rounded">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-700">Title (English)</label>
                    <Input
                      value={resp.titleEn || ''}
                      onChange={(e) => updateResponsibility(index, 'titleEn', e.target.value)}
                      placeholder="e.g., Contract Obligations"
                      className="border-blue-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-700">Description (English)</label>
                    <Input
                      value={resp.descriptionEn || ''}
                      onChange={(e) => updateResponsibility(index, 'descriptionEn', e.target.value)}
                      placeholder="e.g., Comply with employment contract terms..."
                      className="border-blue-300"
                    />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Tips */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>良好僱傭關係要訣 / Tips for Good Employment Relationship</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {content.employerTips.map((tip, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded space-y-4">
                {/* Chinese fields */}
                <div className="flex items-start gap-4">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">標題（中文）</label>
                      <Input
                        value={tip.title}
                        onChange={(e) => updateTip(index, 'title', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">內容（中文）</label>
                      <Input
                        value={tip.content}
                        onChange={(e) => updateTip(index, 'content', e.target.value)}
                      />
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeTip(index)}
                    className="text-red-500 mt-6"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {/* English fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-3 rounded">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-700">Title (English)</label>
                    <Input
                      value={tip.titleEn || ''}
                      onChange={(e) => updateTip(index, 'titleEn', e.target.value)}
                      placeholder="e.g., Clear Expectations"
                      className="border-blue-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-700">Content (English)</label>
                    <Input
                      value={tip.contentEn || ''}
                      onChange={(e) => updateTip(index, 'contentEn', e.target.value)}
                      placeholder="e.g., Clearly communicate job requirements..."
                      className="border-blue-300"
                    />
                  </div>
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addTip}>
              <Plus className="h-4 w-4 mr-2" /> 新增要訣 / Add Tip
            </Button>
          </CardContent>
        </Card>

        {/* Legal Information */}
        <Card>
          <CardHeader style={{ backgroundColor: '#f8fffe' }}>
            <CardTitle style={{ color: '#00b6a5' }}>法例資訊 / Legal Information</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {content.employerLegalInfo.map((info, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={info}
                    onChange={(e) => updateLegalInfo(index, e.target.value)}
                    placeholder="輸入法例資訊（中文）..."
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeLegalInfo(index)}
                    className="text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="bg-blue-50 p-2 rounded">
                  <Input
                    value={content.employerLegalInfoEn?.[index] || ''}
                    onChange={(e) => updateLegalInfoEn(index, e.target.value)}
                    placeholder="Enter legal info (English)..."
                    className="border-blue-300"
                  />
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addLegalInfo}>
              <Plus className="h-4 w-4 mr-2" /> 新增資訊 / Add Info
            </Button>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end pb-24">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            style={{ backgroundColor: '#00b6a5' }}
            className="text-white"
          >
            {saving ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <Save className="h-5 w-5 mr-2" />
            )}
            保存所有更改 / Save All Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminEmployerInfo;
